import { createMutation, request } from '@luna/api';
import { getFeatureConfig } from '@luna/feature-config';
import { getEnv } from '@luna/env';
import { createDeferredSigning } from '@shared/deferred-signing';
import type { SignInfo } from '@shared/core-types';
import type { MyProfile } from '@shared/profile';
import { updateMyProfileCache, useProfileImage } from '@shared/profile';
import { getAppCache } from '@retail/core';

interface ProfileSaveResponse extends Omit<MyProfile, 'signInfo'> {
    signInfo?: SignInfo;
}

const deleteAllOtherUserImages = (offlineKey: string): void => {
    const appCache = getAppCache();

    Object.keys(appCache).forEach((key) => {
        if (key && key.indexOf('userImage') > -1 && key.indexOf(offlineKey) === -1) {
            appCache.remove(key);
        }
    });
};

export const setProfileImage = (offlineKey: string | undefined): void => {
    if (!offlineKey) {
        return;
    }

    const appCacheKey = `userImage-${offlineKey}`;
    const appCache = getAppCache();
    const userImg = appCache.get<string>(appCacheKey);

    if (userImg) {
        useProfileImage.dispatchActions.setImageBase64(userImg);
        useProfileImage.dispatchActions.setImageBigBase64(userImg);
    }

    deleteAllOtherUserImages(offlineKey);
};

export const getProfileWithInstituteId = (profile: MyProfile): MyProfile => {
    if (profile.instituteId) {
        return profile;
    }

    return {
        ...profile,
        instituteId: getFeatureConfig((config) => config.profileConfiguration.defaultInstituteId),
    };
};

export const getProfileUrl = (): string => `${getEnv().prestoUrl}netbanking/my/profile`;

const { useDeferredSigning, deferredSigning } = createDeferredSigning<SignInfo, MyProfile>();
export const useProfileUpdateSigning = useDeferredSigning;

export const [useProfileUpdate] = createMutation<MyProfile, Partial<MyProfile>>({
    name: 'retail/profile-update',
    payloadCreator: async (data) => {
        const { signInfo, ...profile } = await request.put<ProfileSaveResponse>(
            getProfileUrl(),
            data,
            {
                headers: {
                    Accept: '*/*',
                },
            },
        );

        await deferredSigning({ signInfo });

        setProfileImage(profile.offlineKey);

        const profileWithInstituteId = getProfileWithInstituteId(profile);

        updateMyProfileCache({ data: profileWithInstituteId });
        return profileWithInstituteId;
    },
});
